import { useCallback } from 'react';
import { useUpdateEffect } from 'react-use';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';

import { createTranslatedText, FormContextValues, useFormController } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

const sortDoctorsByLastName = (doctorA: DoctorSimple, doctorB: DoctorSimple): 1 | -1 | 0 => {
   const doctorALastName = doctorA.lastName.toLowerCase();
   const doctorBLastName = doctorB.lastName.toLowerCase();
   if (doctorALastName > doctorBLastName) {
      return 1;
   }
   if (doctorALastName < doctorBLastName) {
      return -1;
   }
   return 0;
};

type DoctorSelectFormBean = {
   doctor: DoctorSimple;
};

export const SELECTED_DOCTOR_ID_STORAGE_KEY = 'scheduleSelectedDoctorId';

type DoctorSelectRendererProps = {
   readonly doctors: DoctorSimple[];
   readonly onChange: (doctor: DoctorSimple) => void;
   readonly register: FormContextValues<DoctorSelectFormBean>['register'];
};

// eslint-disable-next-line
const getDoctorLabel = (doctor?: DoctorSimple): string => (doctor ? `${doctor.lastName} ${doctor.firstName}` : '');

const DoctorSelectRenderer = (props: DoctorSelectRendererProps): JSX.Element => {
   const handleDoctorSelect = useCallback(
      (doctor: DoctorSimple | null): void => {
         // No empty selection available
         props.onChange(doctor!);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [props.onChange]
   );
   return (
      <Select
         testId={'doctor'}
         triggerLabelExtractor={getDoctorLabel}
         controller={props.register('doctor')}
         onSelect={handleDoctorSelect}
         compact
      >
         <Select.List<DoctorSimple> items={props.doctors.sort(sortDoctorsByLastName)} keyExtractor={'id'}>
            {(doctor: DoctorSimple) => (
               <Select.Item item={doctor} testId={doctor.id} label={createTranslatedText(getDoctorLabel(doctor))} />
            )}
         </Select.List>
      </Select>
   );
};

type DoctorSelectProps = {
   readonly onChange: (doctor: DoctorSimple) => void;
   readonly doctors: DoctorSimple[];
   readonly selectedDoctor: DoctorSimple;
};

export const DoctorSelect = (props: DoctorSelectProps): JSX.Element => {
   const { register, watch, reset } = useFormController<DoctorSelectFormBean>({
      doctor: props.selectedDoctor,
   });

   const currentDoctor = watch('doctor');

   useUpdateEffect(() => {
      if (props.selectedDoctor.id !== currentDoctor?.id) {
         reset({
            doctor: props.selectedDoctor,
         });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.selectedDoctor]);

   return <DoctorSelectRenderer doctors={props.doctors} onChange={props.onChange} register={register} />;
};
